import { useState, useRef, useEffect } from 'react';
import { MessageSquare, X, Check, CheckCircle } from 'lucide-react';
import { useDAWStore, type CommentMarker } from '../store/daw-store';

interface CommentTrackProps {
  bars: number;
  pixelsPerBeat?: number;
}

export function CommentTrack({ bars, pixelsPerBeat = 16 }: CommentTrackProps) {
  const comments = useDAWStore((state) => state.comments);
  const addComment = useDAWStore((state) => state.addComment);
  const updateComment = useDAWStore((state) => state.updateComment);
  const removeComment = useDAWStore((state) => state.removeComment);
  const currentTime = useDAWStore((state) => state.currentTime);
  const bpm = useDAWStore((state) => state.bpm);
  
  const [creatingComment, setCreatingComment] = useState<{ time: number; x: number } | null>(null);
  const [commentText, setCommentText] = useState('');
  const [selectedComment, setSelectedComment] = useState<CommentMarker | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  
  // Calculate unresolved count (Requirements 3.1, 3.5)
  const unresolvedCount = comments.filter((c) => !c.resolved).length;
  
  // Handle click on comment lane to create new comment (Requirements 3.2)
  const handleLaneClick = (e: React.MouseEvent<HTMLDivElement>) => {
    // Close any open comment popup
    setSelectedComment(null);
    
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    
    // Convert pixel position to beats
    const beatsPerPixel = 1 / pixelsPerBeat;
    const time = x * beatsPerPixel;
    
    setCreatingComment({ time, x });
    setCommentText('');
    
    // Focus textarea after render
    setTimeout(() => textareaRef.current?.focus(), 0);
  };
  
  // Handle comment marker click (Requirements 3.5)
  const handleCommentClick = (comment: CommentMarker, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedComment(comment);
    setCreatingComment(null);
  };
  
  // Toggle comment resolved status (Requirements 4.1)
  const handleToggleResolved = (commentId: string) => {
    const comment = comments.find((c) => c.id === commentId);
    if (comment) {
      updateComment(commentId, { resolved: !comment.resolved });
    }
  };
  
  // Delete comment (Requirements 4.2)
  const handleDeleteComment = (commentId: string) => {
    removeComment(commentId);
    setSelectedComment(null);
  };
  
  // Format timestamp for display
  const formatTimestamp = (timestamp: number) => {
    const date = new Date(timestamp);
    return date.toLocaleString();
  };
  
  // Save comment (Requirements 3.3, 3.4)
  const handleSaveComment = () => {
    if (!creatingComment || !commentText.trim()) return;
    
    // The store's addComment will automatically add id, author, timestamp, and resolved
    // But TypeScript expects all fields except 'id', so we provide them
    addComment({
      time: creatingComment.time,
      text: commentText.trim(),
      author: '', // Will be overridden by store
      timestamp: 0, // Will be overridden by store
      resolved: false, // Will be overridden by store
    });
    
    setCreatingComment(null);
    setCommentText('');
  };
  
  // Cancel comment creation
  const handleCancelComment = () => {
    setCreatingComment(null);
    setCommentText('');
  };
  
  // Handle keyboard shortcuts (Requirements 13.2, 13.3)
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && e.ctrlKey) {
      e.preventDefault();
      handleSaveComment();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleCancelComment();
    }
  };
  
  // Global keyboard shortcuts (Requirements 13.1, 13.4, 13.5)
  useEffect(() => {
    const handleGlobalKeyDown = (e: KeyboardEvent) => {
      // Cmd+Shift+C: Create comment at playhead (Requirements 13.1)
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key.toLowerCase() === 'c') {
        e.preventDefault();
        
        // Convert current time (seconds) to beats
        const beatsPerSecond = bpm / 60;
        const time = currentTime * beatsPerSecond;
        const x = time * pixelsPerBeat;
        
        setCreatingComment({ time, x });
        setCommentText('');
        setSelectedComment(null);
        
        // Focus textarea after render
        setTimeout(() => textareaRef.current?.focus(), 0);
      }
      
      // Delete: Delete selected comment (Requirements 13.4)
      else if (e.key === 'Delete' && selectedComment) {
        e.preventDefault();
        handleDeleteComment(selectedComment.id);
      }
      
      // R: Toggle resolved status (Requirements 13.5)
      else if (e.key.toLowerCase() === 'r' && selectedComment && !creatingComment) {
        // Only trigger if not typing in textarea
        const activeElement = document.activeElement;
        if (activeElement?.tagName !== 'TEXTAREA' && activeElement?.tagName !== 'INPUT') {
          e.preventDefault();
          handleToggleResolved(selectedComment.id);
        }
      }
    };
    
    window.addEventListener('keydown', handleGlobalKeyDown);
    return () => window.removeEventListener('keydown', handleGlobalKeyDown);
  }, [selectedComment, creatingComment, currentTime, bpm, pixelsPerBeat]);
  
  return (
    <div className="flex border-t-2 border-[#3a3a3a]">
      {/* Track header */}
      <div className="w-40 bg-[#1f1f1f] border-r border-[#3a3a3a] px-2 py-2 flex items-center gap-2">
        <MessageSquare size={14} className="text-[#ff6b00]" />
        <div className="flex-1">
          <div className="text-xs">Comments</div>
          {unresolvedCount > 0 && (
            <div className="text-[10px] text-[#ff6b00]">
              {unresolvedCount} unresolved
            </div>
          )}
        </div>
      </div>
      
      {/* Comment lane */}
      <div 
        className="flex-1 relative h-12 bg-[#2a2a2a] cursor-pointer hover:bg-[#2d2d2d] transition-colors"
        onClick={handleLaneClick}
      >
        {/* Grid background */}
        <div className="absolute inset-0 flex pointer-events-none">
          {Array.from({ length: bars }, (_, barIndex) => (
            <div
              key={barIndex}
              className={`border-l ${
                barIndex % 4 === 0 ? 'border-[#4a4a4a]' : 'border-[#2a2a2a]'
              }`}
              style={{ width: `${pixelsPerBeat * 4}px` }}
            />
          ))}
        </div>
        
        {/* Comment markers (Requirements 3.4, 3.5) */}
        <div className="absolute inset-0">
          {comments.map((comment) => {
            const x = comment.time * pixelsPerBeat;
            const isResolved = comment.resolved;
            
            return (
              <div
                key={comment.id}
                className={`absolute top-1/2 -translate-y-1/2 cursor-pointer group transition-all ${
                  isResolved ? 'opacity-60' : ''
                }`}
                style={{ left: `${x}px` }}
                onClick={(e) => handleCommentClick(comment, e)}
                title={`${comment.author}: ${comment.text}`}
              >
                {/* Comment marker icon */}
                <div
                  className={`w-6 h-6 rounded-full flex items-center justify-center border-2 transition-all ${
                    isResolved
                      ? 'bg-gray-600 border-gray-500'
                      : 'bg-[#ff6b00] border-[#ff8533]'
                  } group-hover:scale-110 group-hover:shadow-lg`}
                >
                  {isResolved ? (
                    <CheckCircle size={14} className="text-white" />
                  ) : (
                    <MessageSquare size={14} className="text-white" />
                  )}
                </div>
                
                {/* Hover tooltip (Requirements 4.5) */}
                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                  <div className="bg-[#1f1f1f] border border-[#3a3a3a] rounded px-2 py-1 text-[10px] whitespace-nowrap shadow-lg">
                    <div className="font-medium">{comment.author}</div>
                    <div className="text-gray-400">{formatTimestamp(comment.timestamp)}</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        
        {/* Comment creation popup (Requirements 3.3) */}
        {creatingComment && (
          <div
            className="absolute z-50 bg-[#1f1f1f] border-2 border-[#ff6b00] rounded-lg shadow-xl p-3 w-64"
            style={{
              left: `${creatingComment.x}px`,
              top: '-120px',
              pointerEvents: 'auto',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs font-medium text-[#ff6b00]">
                New Comment at {creatingComment.time.toFixed(2)} beats
              </div>
              <button
                onClick={handleCancelComment}
                className="p-1 hover:bg-[#2a2a2a] rounded"
              >
                <X size={12} />
              </button>
            </div>
            
            <textarea
              ref={textareaRef}
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Enter comment text..."
              className="w-full h-16 bg-[#2a2a2a] border border-[#3a3a3a] rounded px-2 py-1 text-xs resize-none focus:outline-none focus:border-[#ff6b00]"
            />
            
            <div className="flex items-center justify-between mt-2">
              <div className="text-[10px] text-gray-400">
                Ctrl+Enter to save, Esc to cancel
              </div>
              <button
                onClick={handleSaveComment}
                disabled={!commentText.trim()}
                className="px-3 py-1 bg-[#ff6b00] hover:bg-[#ff8533] disabled:bg-[#3a3a3a] disabled:text-gray-500 rounded text-xs flex items-center gap-1 transition-colors"
              >
                <Check size={12} />
                Save
              </button>
            </div>
          </div>
        )}
        
        {/* Comment display popup (Requirements 3.5, 4.1, 4.2) */}
        {selectedComment && (
          <div
            className="absolute z-50 bg-[#1f1f1f] border-2 border-[#ff6b00] rounded-lg shadow-xl p-3 w-72"
            style={{
              left: `${selectedComment.time * pixelsPerBeat}px`,
              top: '-160px',
              pointerEvents: 'auto',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                {selectedComment.resolved ? (
                  <CheckCircle size={14} className="text-gray-400" />
                ) : (
                  <MessageSquare size={14} className="text-[#ff6b00]" />
                )}
                <div className="text-xs font-medium">
                  {selectedComment.author}
                </div>
              </div>
              <button
                onClick={() => setSelectedComment(null)}
                className="p-1 hover:bg-[#2a2a2a] rounded"
              >
                <X size={12} />
              </button>
            </div>
            
            {/* Timestamp */}
            <div className="text-[10px] text-gray-400 mb-2">
              {formatTimestamp(selectedComment.timestamp)} • {selectedComment.time.toFixed(2)} beats
            </div>
            
            {/* Comment text */}
            <div className="bg-[#2a2a2a] border border-[#3a3a3a] rounded px-2 py-2 text-xs mb-3 max-h-24 overflow-y-auto">
              {selectedComment.text}
            </div>
            
            {/* Actions */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => handleToggleResolved(selectedComment.id)}
                className={`flex-1 px-3 py-1.5 rounded text-xs flex items-center justify-center gap-1 transition-colors ${
                  selectedComment.resolved
                    ? 'bg-[#3a3a3a] hover:bg-[#4a4a4a] text-gray-300'
                    : 'bg-green-600 hover:bg-green-700 text-white'
                }`}
              >
                <CheckCircle size={12} />
                {selectedComment.resolved ? 'Unresolve' : 'Mark as Resolved'}
              </button>
              <button
                onClick={() => handleDeleteComment(selectedComment.id)}
                className="px-3 py-1.5 bg-red-600 hover:bg-red-700 rounded text-xs flex items-center gap-1 transition-colors"
              >
                <X size={12} />
                Delete
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
